/**
 * SiteImagesManager — admin panel component that lets admins upload
 * per-slot images used across marketing pages (Home hero, Drive hero,
 * Pitcrew hero, etc). Backed by:
 *
 *   GET    /api/admin/site-images
 *   POST   /api/admin/site-images/{slot}   (multipart)
 *   DELETE /api/admin/site-images/{slot}
 *
 * When an admin uploads a new file the sessionStorage cache used by
 * `useSiteImages` is cleared so the change is visible on the next
 * marketing-page load.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, RefreshCw, Trash2, ImagePlus } from 'lucide-react';
import { clearSiteImagesCache } from '../../hooks/useSiteImages';

const API = process.env.REACT_APP_BACKEND_URL;

// Human-friendly labels + where the slot is used (shown in the card).
const SLOT_META = {
  home_hero_car:        { label: 'Home — Hero Car',         page: '/',                where: 'Big car photo on the home page hero' },
  home_buy_cars_tile:   { label: 'Home — Buy Cars Tile',    page: '/',                where: 'Image inside the "Buy a Used Car" tile' },
  home_service_tile:    { label: 'Home — Service Tile',     page: '/',                where: 'Image inside the "Service Your Car" tile' },
  home_franchise_image: { label: 'Home — Franchise Image',  page: '/',                where: 'Image on the "Own a Propitstop Franchise" section (above Businesses)' },
  drive_hero_mockup:    { label: 'Drive — Hero Mockup',     page: '/business/drive',  where: 'Product mockup on the Drive hero section' },
  drive_website_free_banner: { label: 'Drive — Website/Journey Banner', page: '/business/drive', where: 'Full-width AI-Powered Inventory + Customer Journey banner mid-page' },
  pitcrew_hero_mockup:  { label: 'Pitcrew — Hero Mockup',   page: '/business/pitcrew',where: 'Product mockup on the Pitcrew hero section' },
  used_cars_banner:     { label: 'Used Cars — Top Banner',  page: '/used-cars',       where: 'Optional promo banner above the listings grid' },
  franchise_page_poster: { label: 'Franchise — Full Poster', page: '/franchise',      where: 'Full-page franchise poster (buttons are overlaid on it automatically)' },
};

const abs = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API}/api${url.startsWith('/') ? '' : '/'}${url}`;
};

export default function SiteImagesManager() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ slots: [], images: {} });
  const [busySlot, setBusySlot] = useState(null);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` });

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/admin/site-images`, { headers: authHeader() })
      .then((r) => setState(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load images'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (slot, file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image is too large (max 10 MB).');
      return;
    }
    setBusySlot(slot);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/api/admin/site-images/${slot}`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      clearSiteImagesCache();
      toast.success('Image updated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusySlot(null);
    }
  };

  const clearSlot = async (slot) => {
    if (!window.confirm('Reset this image back to the default?')) return;
    setBusySlot(slot);
    try {
      await axios.delete(`${API}/api/admin/site-images/${slot}`, { headers: authHeader() });
      clearSiteImagesCache();
      toast.success('Image reset');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setBusySlot(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="site-images-manager">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Content</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 mt-1">Site Images</h2>
          <p className="text-sm text-zinc-500 mt-1 max-w-lg">
            Upload custom images for key spots across the marketing site. Changes take effect the next time visitors open the page.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 bg-white text-sm text-zinc-700 hover:border-zinc-500"
          data-testid="site-images-refresh"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="min-h-[240px] flex items-center justify-center bg-white border border-zinc-200 rounded-xl">
          <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {state.slots.map((slot) => (
            <SlotCard
              key={slot}
              slot={slot}
              meta={SLOT_META[slot] || { label: slot, page: '', where: '' }}
              record={state.images[slot] || {}}
              busy={busySlot === slot}
              onUpload={(file) => upload(slot, file)}
              onClear={() => clearSlot(slot)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SlotCard({ slot, meta, record, busy, onUpload, onClear }) {
  const inputRef = useRef(null);
  const current = record?.url ? abs(record.url) : null;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden flex flex-col" data-testid={`site-image-slot-${slot}`}>
      <div className="aspect-[16/10] bg-zinc-100 flex items-center justify-center">
        {current ? (
          <img src={current} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="text-center text-zinc-400">
            <ImagePlus className="w-8 h-8 mx-auto" />
            <div className="text-xs mt-1">Using page default</div>
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-sm font-bold text-zinc-900">{meta.label}</div>
        <div className="text-[11px] text-zinc-500 mt-0.5">{meta.page}</div>
        <div className="text-xs text-zinc-500 mt-2 leading-snug">{meta.where}</div>
        {record?.updated_at && (
          <div className="text-[10px] text-emerald-600 mt-2 font-semibold">
            Updated {new Date(record.updated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
        <div className="mt-4 flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])}
            data-testid={`site-image-input-${slot}`}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold disabled:opacity-60"
            data-testid={`site-image-upload-${slot}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {current ? 'Replace' : 'Upload'}
          </button>
          {current && (
            <button
              onClick={onClear}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-zinc-300 hover:border-red-400 text-xs font-bold text-red-600 disabled:opacity-60"
              data-testid={`site-image-clear-${slot}`}
            >
              <Trash2 className="w-3.5 h-3.5" /> Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
